import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Subject, Subscription, of, switchMap, takeUntil } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { formatStat, formatStatOneDecimal, snakeCaseToTitleCase, wrapWithPrefixSuffix } from '../../utils/format.utils';

@Component({
  selector: 'app-number-stats-shared',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './number-stats.component.html',
  styleUrl: './number-stats.component.scss',
})
export class NumberStatsComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  stat!: string;
  statInputValue: number | string | null = null;

  private traceKey = '';
  private filterSub!: Subscription;
  private unSubscribe$ = new Subject<void>();

  public hasError = false;
  public errorMessage = '';

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
  ) {}

  ngAfterViewInit(): void {
    this.traceKey = `NUMBER_STATS_TRACE::shared::${this.data?.widgetId || 'unknown'}::${this.data?.key || 'unknown'}`;
    console.info(`[${this.traceKey}] init`, {
      widgetId: this.data?.widgetId,
      key: this.data?.key,
      title: this.data?.title,
      apiUrl: this.data?.apiUrl,
      customPayload: this.data?.customPayload,
    });

    this.filterSub = this.loadStat(this.getInitialFiltersForWidget())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => {
          console.error('Error loading stat:', err);
        },
      });

    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          if (this.data.callApiInDefault === false) {
            let mandatoryFiltersSelected = true;
            const missingMandatoryFilterNames: string[] = [];

            if (this.data.mandatoryFilters && this.data.mandatoryFilters.length > 0) {
              for (const mandatoryFilterKey of this.data.mandatoryFilters) {
                const filterValue =
                  filters.queryParams[mandatoryFilterKey] ||
                  filters.requestParams[mandatoryFilterKey];
                if (!filterValue || filterValue.length === 0) {
                  mandatoryFiltersSelected = false;
                  missingMandatoryFilterNames.push(snakeCaseToTitleCase(mandatoryFilterKey));
                }
              }
            }

            if (!mandatoryFiltersSelected) {
              this.hasError = true;
              this.errorMessage = `Please select ${missingMandatoryFilterNames.join(' and ')} to see the data.`;
              return of(null);
            }
          }

          this.hasError = false;
          return this.loadStat(filters);
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (res === null) return;

          try {
            this.updateStat(res);

            if (!this.stat) {
              this.hasError = true;
              this.errorMessage = 'No data found for the selected filters.';
            } else {
              this.hasError = false;
            }
          } catch {
            this.hasError = true;
            this.errorMessage = 'Error processing data.';
          }
        },
        error: (err: any) => {
          this.hasError = true;
          this.errorMessage = 'Error loading stat.';
          console.error('Error loading stat:', err);
        },
      });
  }

  get formattedTitle(): string {
    return this.data?.title?.replace(/\n/g, '<br>') || '';
  }

  get alignmentClass(): string {
    const alignment = String(this.data?.alignment || this.data?.contentAlignment || 'center').toLowerCase();

    if (alignment === 'left' || alignment === 'start') {
      return 'align-left';
    }

    if (alignment === 'right' || alignment === 'end') {
      return 'align-right';
    }

    return 'align-center';
  }

  get isInputNumberStat(): boolean {
    return this.data?.inputNumberStat === true;
  }

  onStatInputChange(value: number | string | null): void {
    this.statInputValue = value;
    this.stat = String(value ?? '');
  }

  onInputStatEnter(): void {
    if (!this.isInputNumberStat) {
      return;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters();
    const requestParams = {
      ...(currentFilters?.requestParams || {}),
      [this.data?.key]: this.statInputValue,
    };

    this.loadStat({
      ...currentFilters,
      requestParams,
      useQueryParams: this.data?.useQueryParams || false,
    })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => {
          console.error('Error loading stat:', err);
          this.hasError = true;
          this.errorMessage = 'Error loading stat.';
        },
      });
  }

  loadStat(filters?: any): any {
    const requestFilters = {
      ...filters,
      useQueryParams: this.data?.useQueryParams || false,
    };

    console.info(`[${this.traceKey}] loadStat`, {
      requestFilters,
      method: this.data?.method || 'post',
      customPayloadEnabled: !!this.data?.customPayload,
    });

    return this.configService.getConfig(
      this.data?.apiUrl,
      this.data,
      requestFilters,
      this.data?.method || 'post',
    );
  }

  private getInitialFiltersForWidget(): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(this.data?.widgetId);
    if (navigationFilters) {
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters) {
      return undefined;
    }

    return this.chartFilterHandler.shouldReload(filters, this.data?.widgetId) ? filters : undefined;
  }

  private updateStat(res: any): void {
    const configuredPath = String(this.data?.dataPath || this.data?.responsePath || '').trim();
    const hasDirectStatKey = !!this.data?.key
      && res
      && typeof res === 'object'
      && !Array.isArray(res)
      && Object.prototype.hasOwnProperty.call(res, this.data.key);
    let normalized = hasDirectStatKey
      ? res
      : configuredPath
        ? this.getByPath(res, configuredPath)
        : res?.responseList?.[0]?.data?.[0] ?? res;
    
    // Handle unwrapped array response (multiple items in responseList[0].data)
    if (Array.isArray(normalized)) {
      normalized = normalized[0] ?? {};
    }
    
    const parent = this.data?.parentKey
      ? normalized?.[this.data?.parentKey] ?? res?.[this.data?.parentKey]
      : normalized;
    const source = parent ?? normalized;
    const rawValue = source?.[this.data.key];

    console.info(`[${this.traceKey}] updateStat`, {
      rawResponse: res,
      hasDirectStatKey,
      configuredPath,
      normalized,
      parentKey: this.data?.parentKey,
      source,
      lookupKey: this.data?.key,
      rawValue,
    });

    this.statInputValue = rawValue ?? null;
    this.stat = this.formatValue(rawValue);

    console.info(`[${this.traceKey}] renderedStat`, {
      stat: this.stat,
      useFormatStat: this.data?.useFormatStat,
      prefix: this.data?.prefix,
    });
  }

  protected formatValue(value: any): string {
    const useAffixes = this.data?.applyAffixes === true;

    if (this.data?.useFormatStatOneDecimal) {
      const formatted = formatStatOneDecimal(value, this.data?.key || '', this.data?.prefix || '');
      return useAffixes ? wrapWithPrefixSuffix(formatted, '', this.data?.suffix || '') : formatted;
    }

    if (this.data?.useFormatStat) {
      const formatted = formatStat(value, this.data?.key || '', this.data?.prefix || '');
      return useAffixes ? wrapWithPrefixSuffix(formatted, '', this.data?.suffix || '') : formatted;
    }

    const rawValue = String(value ?? '');
    return useAffixes
      ? wrapWithPrefixSuffix(rawValue, this.data?.prefix || '', this.data?.suffix || '')
      : rawValue;
  }

  private getByPath(source: any, path: string): any {
    return path.split('.').reduce((current: any, segment: string) => current?.[segment], source);
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
